import { useOpenBillingPortal, useStartCheckout, useSubscription } from "../../api/queries";
import { isUnauthorized } from "../../api/errors";
import { useAuth } from "../../auth/AuthContext";
import { canSeeBilling } from "../../auth/roleView";
import { EmptyState, ErrorState, LoadingState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { PageHeader } from "../../components/PageHeader";
import { useToast } from "../../toast/ToastContext";
import { LicenseSection } from "./LicenseSection";
import "./billing.css";

/**
 * Subscription + money path (L-1), reachable by `admin` and `billing_admin`.
 *
 * It exists so that paying for Comebye does not require an admin account (and
 * with it `container:exec`/`container:escalate` on every host). The gate below
 * is UX only — the server enforces `billing:view`/`billing:manage` on every
 * request (S-8).
 *
 * The checkout/portal URLs are short-lived bearer capabilities: this component
 * navigates to them and never stores, logs, or renders them (S-13).
 */
function goTo(url: string) {
  window.location.assign(url);
}

function SubscriptionCard() {
  const { pushToast } = useToast();
  const { data, isPending, isError, error, refetch } = useSubscription();
  const checkout = useStartCheckout();
  const portal = useOpenBillingPortal();
  const busy = checkout.isPending || portal.isPending;

  if (isPending) return <LoadingState label="Loading your subscription…" />;
  if (isError) {
    if (isUnauthorized(error)) return <LoadingState label="Loading your subscription…" />;
    return (
      <ErrorState
        description={
          error instanceof Error ? error.message : "Couldn't load your subscription. Check your connection and try again."
        }
        onRetry={refetch}
      />
    );
  }
  if (!data) return null;

  function fail(title: string) {
    return (err: unknown) => {
      pushToast({
        title,
        description: err instanceof Error ? err.message : "Try again in a moment.",
        tone: "error",
      });
    };
  }

  return (
    <>
      <dl className="billing-summary">
        <div className="billing-summary__row">
          <dt>Plan</dt>
          <dd>{data.tier}</dd>
        </div>
        <div className="billing-summary__row">
          <dt>Status</dt>
          <dd>{data.status}</dd>
        </div>
        <div className="billing-summary__row">
          <dt>Machines billed</dt>
          {/* Absent and zero are different claims: the server omits the count
              when its meter is unavailable, and telling someone about to be
              charged "0 machines" when we do not know would be a lie. */}
          <dd>
            {data.nodeCount === undefined ? "Unavailable" : data.nodeCount.toLocaleString()}
            {" of "}
            {data.nodeCapacity.toLocaleString()} included
          </dd>
        </div>
        {data.currentPeriodEnd && (
          <div className="billing-summary__row">
            <dt>Current period ends</dt>
            <dd>{new Date(data.currentPeriodEnd).toLocaleDateString()}</dd>
          </div>
        )}
      </dl>

      <div className="billing-actions">
        {data.subscribed ? (
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => portal.mutate(undefined, { onSuccess: goTo, onError: fail("Couldn't open the billing portal") })}
          >
            {portal.isPending ? "Opening…" : "Manage subscription"}
          </Button>
        ) : (
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => checkout.mutate(undefined, { onSuccess: goTo, onError: fail("Couldn't start checkout") })}
          >
            {checkout.isPending ? "Starting…" : "Start a subscription"}
          </Button>
        )}
      </div>
      <p className="billing-note">
        Payments are handled by Stripe. Comebye never sees or stores your card details.
      </p>
    </>
  );
}

export function BillingPage() {
  const { session } = useAuth();

  return (
    <>
      <PageHeader title="Billing" description="Your plan, the machines you're billed for, and your payment method." />
      {canSeeBilling(session?.role) ? (
        <>
          <section className="billing-section license-summary" aria-label="Plan and licensing">
            <LicenseSection />
          </section>
          <section className="billing-section" aria-label="Subscription">
            <SubscriptionCard />
          </section>
        </>
      ) : (
        <EmptyState
          title="No billing access"
          description="Billing is limited to admin and billing admin accounts. Ask an admin if you need access."
        />
      )}
    </>
  );
}
